import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import {
  SlotDefinition,
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
  UniversalSlotPointer,
} from '@wix/editor-platform-host-integration-apis';
import {
  getPluginInfoByCompRef,
  createWidgetPluginComponentDefinition,
  buildComponentPreviewState,
  getEditorPreviewFrame,
  getContainer_DEPRECATED_BAD_PERFORMANCE,
  getSlotDefinitions,
  filterPublicSlots,
  getBuilderAppData,
} from '../utils';
import {
  VIRTUAL_OOI_SLOT_SEPARATOR,
  ComponentPreviewState,
  SUPPORTED_TPA_HOST_PLUGIN_COMPONENT_TYPES,
} from '../constants';
import { PluginHostApiImplementation } from '../types';

import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { SlotPointer } from '../slotPointer';
import { getAppDataByCompRef } from '../utils/getAppData';

interface SlotFromAppManifest {
  displayName: string;
  name: string;
  isPublic: true;
}

function getVirtualSlotPlaceholderData(compRef: CompRef): {
  widgetHostRef: CompRef;
  slotName: string;
} {
  const [hostId, slotName] = compRef.id.split(VIRTUAL_OOI_SLOT_SEPARATOR);

  return {
    widgetHostRef: {
      id: hostId,
      type: compRef.type,
    },
    slotName,
  };
}

function getSlotsFromAppManifest(
  documentServicesAPI: DocumentServicesObject,
  widgetCompRef: CompRef,
): SlotDefinition[] | SlotFromAppManifest[] {
  const { appDefinitionId, widgetId } =
    documentServicesAPI.components.data.get(widgetCompRef);

  if (!appDefinitionId) {
    return [];
  }

  const appManifest =
    documentServicesAPI.platform.getAppManifest(appDefinitionId);

  if (!appManifest) {
    return [];
  }

  const slotsMap: Record<string, Pick<SlotDefinition, 'displayName'>> =
    // @ts-expect-error
    appManifest.controllersStageData?.[widgetId]?.default?.slots ?? {};

  return Object.entries(slotsMap).map(([slotName, slotConfig]) => ({
    name: slotName,
    ...slotConfig,
    isPublic: true,
  }));
}

function getVirtualSlotsPlaceholderCompRef(
  widgetCompRef: CompRef,
  slotName: string,
): CompRef {
  return {
    id: `${widgetCompRef.id}${VIRTUAL_OOI_SLOT_SEPARATOR}${slotName}`,
    type: widgetCompRef.type,
  };
}

function getWidgetSlotData(
  documentServicesAPI: DocumentServicesObject,
  hostRef: CompRef,
  slot: {
    displayName: string;
    name: string;
    isPublic: boolean;
    id?: string; // optional untill we fully migrate from placements
  },
): WidgetSlot {
  const slotName = slot.name;
  const { widgetId, appDefinitionId } =
    documentServicesAPI.components.data.get(hostRef);

  const pluginCompRef =
    documentServicesAPI.components.slots.getSlotsData(hostRef)?.[slotName];

  return {
    hostCompRef: hostRef,
    compRef: getVirtualSlotsPlaceholderCompRef(hostRef, slotName),
    role: slotName,
    id: slot.id,
    pluginInfo: pluginCompRef
      ? getPluginInfoByCompRef(documentServicesAPI, pluginCompRef)
      : undefined,
    placement: {
      appDefinitionId,
      widgetId,
      slotId: slotName,
    },
  };
}

export function createTpaHostAPI(
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): PluginHostApiImplementation {
  function getSlotDefinitionsWithFallback(
    widgetCompRef: CompRef,
    options: { includePrivate: boolean },
  ): SlotDefinition[] | SlotFromAppManifest[] {
    const slotDefinitions = getSlotDefinitions(
      documentServicesAPI,
      widgetCompRef,
      experimentsAPI,
    );
    if (slotDefinitions.length > 0) {
      return options.includePrivate
        ? slotDefinitions
        : filterPublicSlots(slotDefinitions);
    }
    // fallback to reading from AppManifest
    return getSlotsFromAppManifest(documentServicesAPI, widgetCompRef);
  }

  return {
    getWidgetHostRefBySlotContainer(slotPointer: SlotPointer): CompRef {
      const slotRef = slotPointer.toOOI().slotRef;
      return this.getWidgetHostRef(slotRef);
    },
    getSlotPlaceholderDisplayName(
      virtualSlotPlaceholderRef: CompRef,
    ): string | undefined {
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );
      const slotsPlaceholders = getSlotsFromAppManifest(
        documentServicesAPI,
        widgetHostRef,
      );
      const slotPlaceholder = slotsPlaceholders.find(
        (s) => s.name === slotName,
      );
      return slotPlaceholder?.displayName;
    },
    getWidgetSlot(
      slotPointer: SlotPointer,
      { includePrivate }: { includePrivate: boolean },
    ): WidgetSlot {
      const virtualSlotsPlaceholderRef = slotPointer.toOOI().slotRef;
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        virtualSlotsPlaceholderRef,
      );
      const slots = getSlotDefinitionsWithFallback(widgetHostRef, {
        includePrivate,
      });
      const slot = slots.find((slot) => slot.name === slotName) ?? {
        displayName: slotName,
        name: slotName,
        isPublic: true,
      };
      return getWidgetSlotData(documentServicesAPI, widgetHostRef, slot);
    },
    getWidgetSlots(
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ): WidgetSlot[] {
      const slots = getSlotDefinitionsWithFallback(widgetCompRef, options);
      return slots.map((slot) =>
        getWidgetSlotData(documentServicesAPI, widgetCompRef, slot),
      );
    },
    getSlotPosition(
      virtualSlotPlaceholderRef: CompRef,
    ): WidgetSlotPosition | null {
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );

      const virtualSlotPlaceholderID = `${widgetHostRef.id}.${slotName}`;
      const previewFrame = getEditorPreviewFrame();
      const virtualSlotPlaceholderElement =
        previewFrame.contentWindow?.document.getElementById(
          virtualSlotPlaceholderID,
        );

      if (!virtualSlotPlaceholderElement) {
        return null;
      }

      const rect = virtualSlotPlaceholderElement.getBoundingClientRect();
      return {
        y: rect.y,
        height: rect.height,
      };
    },
    getSlotContentRef(slotPointer: SlotPointer): CompRef | undefined {
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        slotPointer.toOOI().slotRef,
      );
      return documentServicesAPI.components.slots.getSlotsData(widgetHostRef)[
        slotName
      ];
    },
    getCompRefForSlotSelection(slotPointer: SlotPointer): CompRef | undefined {
      return this.getSlotContentRef(slotPointer);
    },
    isWidgetSlotPopulated(slotPointer: SlotPointer): boolean {
      return Boolean(this.getSlotContentRef(slotPointer));
    },
    async populateWidgetPluginToSlot(
      slotPointer: SlotPointer,
      widgetPointer: WidgetPointer,
    ): Promise<CompRef> {
      const virtualSlotPlaceholderRef = slotPointer.toOOI().slotRef;
      const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
        widgetPointer.appDefinitionId,
      );
      const widgetComponent = await createWidgetPluginComponentDefinition(
        documentServicesAPI,
        widgetPointer,
        appData.applicationId!,
        experimentsAPI,
      );

      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );

      const compRef: CompRef = documentServicesAPI.components.slots.populate(
        widgetHostRef,
        slotName,
        widgetComponent,
      );

      return compRef;
    },
    getWidgetHostRef(virtualSlotPlaceholderRef: CompRef): CompRef {
      const { widgetHostRef } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );

      return widgetHostRef;
    },
    uninstallWidgetPlugin(slotPointer: SlotPointer) {
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        slotPointer.toOOI().slotRef,
      );

      documentServicesAPI.components.slots.remove(widgetHostRef, slotName);
    },
    getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined {
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        slotPointer.toOOI().slotRef,
      );

      const contentRef =
        documentServicesAPI.components.slots.getSlotsData(widgetHostRef)[
          slotName
        ];

      if (!contentRef) {
        return undefined;
      }

      const { appDefinitionId, widgetId } = getAppDataByCompRef(
        documentServicesAPI,
        contentRef,
      );

      return { appDefinitionId, widgetId };
    },
    /**
     * @deprecated Will be deprecated in upcoming PRs — use
     * `getUniversalSlotPointerByPluginCompRef` instead.
     */
    getSlotPlaceholderRefByContentRef(
      widgetPluginCompRef: CompRef,
    ): CompRef | undefined {
      const hostWidgetRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
        documentServicesAPI,
        widgetPluginCompRef,
      );

      if (!hostWidgetRef) {
        return undefined;
      }

      const hostSlots =
        documentServicesAPI.components.slots.getSlotsData(hostWidgetRef);
      const [pluginSlotName] =
        Object.entries(hostSlots).find(
          ([, slotContentRef]) => slotContentRef?.id === widgetPluginCompRef.id,
        ) ?? [];

      return pluginSlotName
        ? getVirtualSlotsPlaceholderCompRef(hostWidgetRef, pluginSlotName)
        : undefined;
    },
    getUniversalSlotPointerByPluginCompRef(
      widgetPluginCompRef: CompRef,
    ): UniversalSlotPointer | undefined {
      const hostWidgetRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
        documentServicesAPI,
        widgetPluginCompRef,
      );

      if (!hostWidgetRef) {
        return undefined;
      }

      const hostSlots =
        documentServicesAPI.components.slots.getSlotsData(hostWidgetRef);
      const [pluginSlotName] =
        Object.entries(hostSlots).find(
          ([, slotContentRef]) => slotContentRef?.id === widgetPluginCompRef.id,
        ) ?? [];

      if (!pluginSlotName) {
        return undefined;
      }

      const slotDefinition = getSlotDefinitions(
        documentServicesAPI,
        hostWidgetRef,
        experimentsAPI,
      ).find((definition) => definition.name === pluginSlotName);

      if (!slotDefinition) {
        return undefined;
      }

      return {
        hostCompRef: hostWidgetRef,
        slotId: slotDefinition.id,
      };
    },
    isSlotPublic(slotPointer: SlotPointer): boolean {
      const slotCompRef = slotPointer.toOOI().slotRef;
      const { widgetHostRef, slotName } =
        getVirtualSlotPlaceholderData(slotCompRef);
      const slots = getSlotDefinitionsWithFallback(widgetHostRef, {
        includePrivate: true,
      });
      const slot = slots.find((s) => s.name === slotName);
      return slot?.isPublic === true;
    },
    hasWidgetSlot(widgetCompRef: CompRef): boolean {
      const slots = getSlotDefinitionsWithFallback(widgetCompRef, {
        includePrivate: false,
      });
      return slots.length > 0;
    },
    async highlightSlot(slotPointer: SlotPointer) {
      const virtualSlotPlaceholderRef = slotPointer.toOOI().slotRef;
      const { widgetHostRef, slotName } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );
      documentServicesAPI.documentMode.setComponentPreviewState(
        widgetHostRef.id,
        buildComponentPreviewState(slotName, ComponentPreviewState.HIGHLIGHTED),
      );
      await documentServicesAPI.waitForChangesAppliedAsync();
    },
    unhighlightSlot(slotPointer: SlotPointer) {
      const virtualSlotPlaceholderRef = slotPointer.toOOI().slotRef;
      const { widgetHostRef } = getVirtualSlotPlaceholderData(
        virtualSlotPlaceholderRef,
      );
      documentServicesAPI.documentMode.setComponentPreviewState(
        widgetHostRef.id,
        undefined,
      );
    },
    isPluginComponent(compRef: CompRef): boolean {
      const parentRef = getContainer_DEPRECATED_BAD_PERFORMANCE(
        documentServicesAPI,
        compRef,
      );

      if (!parentRef) {
        return false;
      }
      const { isBuilder } = getBuilderAppData(compRef, documentServicesAPI);
      const hostRef =
        documentServicesAPI.components.slots.builder.getSlottedComponentOwner(
          compRef,
        );

      // builder plugin check
      if (isBuilder && hostRef.compPointer.id === parentRef.id) {
        return true;
      }

      // other plugins check
      const compType = documentServicesAPI.components.getType(parentRef);
      return SUPPORTED_TPA_HOST_PLUGIN_COMPONENT_TYPES.includes(compType);
    },
  };
}
